<script setup lang="ts">
import { computed, ref } from "vue";
import { useAppStore } from "@/store/app";
import { sendUserBirthday, sendActivationEmail, sendComplaint } from '@/services/reportService';
import { getUserAge, getFieldTag, maxDate } from '@/services/useModal';

const appStore = useAppStore();
const isModalActive = computed(() => appStore.getModalVisibility);
const inputDataSource = computed(() => appStore.getInputDataSource);
const isButtonDisabled = computed(() => appStore.getButtonDisabled);
const service = computed(() => appStore.getService);
const birthday = ref("");
const birthdayError = ref("");
const modalSchema = computed(() => {
  const currentVariant = appStore.getModalData.modalVariant;
  const error = appStore.getModalData.errorMessage;
  let modalSchema = appStore.getModal(currentVariant);
  // Logika wyboru tekstu przycisku na podstawie źródła wywołania modala czy z formularza czy z urla
  const button  = inputDataSource.value === 'form' ? modalSchema.button : 'Wróć na stronę';

  //jesli BE zwróci error to wstawimy modalowi tekst z błędem
  if(currentVariant === 'something_wrong' || currentVariant === 'something_wrong_mail') {
    modalSchema = {
      ...modalSchema,
      text: error,
      button: button
    }
  }

  return modalSchema;
});

const proxyClickAction = (action:string) => {
  console.log('wywołanie akcji: ', action);
  if (action === 'CLOSE' || action === 'BACK' || action === undefined) {
    appStore.closeElements('all')
  } else if(action === 'SEND_COMPLAINT'){
    submitComplaint();
  } else if(action === 'SEND_NEW_BIRTHDAY_DATE'){
    submitBirthday();
  } else if(action === 'SEND_ACTIVATION_EMAIL_AGAIN'){
    console.log('SEND_ACTIVATION_EMAIL_AGAIN');
    resendActivationMail();
  } else if(action === 'RESUBMIT_FORM'){
    console.log('odpalam RESUBMIT_FORM');
    appStore.submitForm();
  } else if(action === 'RESUBMIT_COMPLAINT'){
    submitComplaint();
  } else if(action === 'TEST_ACTION'){
    console.log('odpalam TEST_ACTION');
  } else {
    // sendActionData(action);
    appStore.closeElements('all')
  }
};

const inputActionMethod = (input: any, value: string) => {
  const action = input.actionMethod;
  console.log(`Wykonaj akcje: ${action} z wartością ${value}`);

  if (action === 'UPDATE_BIRTHDAY') {
    checkBirthDayInput(value);
  }

  if (action === 'TEXT_INPUT_ACTION') {
    console.log('Wywołanie funkcji TEXT_INPUT_ACTION', value, input);
  }

  if (action === 'checkText' && input.name === 'complaint_reason') {
    appStore.setComplaintReason(value);

    if (value.trim().length === 0) {
      input.helperText = '';
      input.error = false;
    } else {
      const { valid, message } = validateComplaintText(value);
      input.helperText = message;
      input.error = !valid;
    }
  }
};

const validateComplaintText = (value: string) => {
  const length = value.trim().length;

  if (length < 10) {
    return { valid: false, message: 'Minimalna liczba znaków wynosi 10.' };
  }
  if (length > 500) {
    return { valid: false, message: 'Maksymalna liczba znaków wynosi 500.' };
  }
  return { valid: true, message: '' };
};

const getComplaintInput = () =>
  modalSchema.value?.inputs?.find((i: any) => i.name === 'complaint_reason');

const submitComplaint = async () => {
  const value = appStore.getComplaintReason;
  const { valid, message } = validateComplaintText(value);
  
  const complaintInput = getComplaintInput();
  if (complaintInput) {
    complaintInput.error = !valid;
    complaintInput.helperText = message;
  }

  if (!valid) {
    return;
  }

  appStore.setButtonDisabled(true);

  try {
    const response = await sendComplaint(value.trim(), appStore.getComplaintCode);

    appStore.setModalData({
      show: true,
      modalVariant: response.modal === 'something_wrong' ? 'complaint_something_wrong' : response.modal,
      errorMessage: '',
    });
  } finally {
    appStore.setButtonDisabled(false);
  }
};

const getFieldAttrs = (input: { type?: string; name: string }) => {
  const attrs: Record<string, string> = {
    id: input.name,
    name: input.name,
  };

  if (input.type && input.type !== 'textarea' && input.type !== 'select') {
    attrs.type = input.type;
  }

  return attrs;
}

const addExtraOptions = (name:string) => {
  const options = {};

  if(name === 'birthday'){
    const today = new Date();
    return {
      max: maxDate(today),
      min: '1900-01-01',
      autocorrect: "off",
      autocomplete: "off",
    }
  }

  return options;
}

const checkBirthDayInput = (value:string) => {
  const dateInput = document.querySelector('input[name="birthday"]') as HTMLInputElement;
  if(!dateInput) return false;

  const userAge = getUserAge(value);
  let errorMessage = '';
  let valid = false;


  if (value.length === 0) {
    dateInput.classList.add('dsaModal__input--error');
    errorMessage = "Podaj date urodzenia";
    valid = false;
  } else if(userAge < 16) {
    dateInput.classList.add('dsaModal__input--error');
    errorMessage = "Podana data urodzenia jest nieprawidłowa. Minimalny wymagany wiek to 16 lat";
    valid = false;
  } else if(userAge > 120) {
    dateInput.classList.add('dsaModal__input--error');
    errorMessage = "Podana data urodzenia jest nieprawidłowa. Sprawdź czy data została poprawnie wpisana";
    valid = false;
  } else{
    dateInput.classList.remove('dsaModal__input--error');
    errorMessage = "";
    valid = true;
  }
  birthday.value = value;
  birthdayError.value = errorMessage;
  
  return valid;
}

const submitBirthday = async () => {
  const isValid = checkBirthDayInput(birthday.value);

  if (!isValid) {
    return;
  }

  appStore.setButtonDisabled(true);

  try {
    const response = await sendUserBirthday(birthday.value);
    console.log('submitBirthday', response);
    if (response.result === true) {
      
      appStore.setModalData({
        modalVariant: "birthday_thankYou", 
        show: true,
        errorMessage: ""
      });
 
    } else {
      console.log('nie udało sie zapisac daty i powinien pokazac sie komunikat z Nie udało się zapisać daty'); //lukixp
      appStore.setModalData({
        modalVariant: "something_wrong_mail", 
        show: true,
        errorMessage: "Nie udało się zapisać daty."
      });
    }
  } finally {
    appStore.setButtonDisabled(false);
  }
};

const resendActivationMail = async () => {
  appStore.setButtonDisabled(true); 

  try {
    const response = await sendActivationEmail();
    console.log('Po kliknieciu wyslij ponownie maila z linkiem aktywacyjnym:', response);
   
    if (response.responseCode === 0) {
      console.log("Pomyślnie wysłano maila aktywacyjnego.");
      appStore.closeElements('all');
    } else if (response.responseCode === 101) {
      if (response.birthDateMissing) {
      console.warn("Brakuje daty urodzenia! Przełączam na formularz daty. patab3");
      
      appStore.setModalData({ 
        show: true,
        modalVariant: 'birthday_form' 
      });

      } else {
        appStore.setModalData({ 
          show: true,
          modalVariant: 'something_wrong_mail', 
          errorMessage: "Konto jest już aktywne." 
        });
      }
    } else if (response.responseCode === 102) {
      
      appStore.setModalData({ 
        show: true,
        modalVariant: 'something_wrong_mail', 
        errorMessage: "Nie mamy Twojego adresu mailowego" 
      });

    } else if (response.responseCode === 403) {

      appStore.setModalData({ 
        show: true,
        modalVariant: 'something_wrong_mail', 
        errorMessage: "Obecnie serwery są zajęte. Poczekaj 1h aby wysłać email ponownie." 
      });

    } else {
      console.error("Błąd ponownej wysyłki maila:", response.debugMsg);

      appStore.setModalData({ 
        modalVariant: 'something_wrong_mail',
        show: true,
        errorMessage: "Nie udało się ponownie wysłać maila. Spróbój ponownie później" 
      });

    }
  } catch (err) {
    console.error("Wyjątek podczas wysyłki maila:", err);
    
    appStore.setModalData({ 
      modalVariant: 'something_wrong_mail',
      show: true, 
      errorMessage: "Wystąpił błąd połączenia." 
    });
    
  } finally {
    appStore.setButtonDisabled(false); 
  }
};

//funkcja dla modali akcji ale dopiero po ujednoliceniu i przeniesieniu useModeratorFormLogic do wspólnego modułu Modal
// const sendActionData = (action:string) => {
//   const modalData = appStore.getModalData;
//   const actionData = {
//     "source": service.value,
//     "reason": modalData.reason,
//     "decisionBase": modalData.decisionBase,
//     "commentIds": modalData.commentsID,
//     "newStatus": action, //DELETED, PUBLISHED, CENSORED, WAITING, HIGHLIGHTED, TERRORIST, REDACTION
//     "metaIds": modalData.metaIds || [],
//     "violationReport": modalData.violationReportId === 'Zgłoszenie innego użytkownika'
//   }

//   console.log('Wysli dane akcji z forum do BE', actionData );
// }

</script>

<template>
  <div
    class="dsaModal"
    :class="[modalSchema?.extraClass, modalSchema.type === 'dsa_action' ? 'dsaModal--moderation' : '']"
    v-if="isModalActive"
  >
    <div @click="() => appStore.closeElements('all')" class="dsaModal__close"></div>

    <div v-if="modalSchema.header" class="dsaModal__header">{{ modalSchema.header }}</div>

    <div v-if="modalSchema.text" class="dsaModal__text">{{ modalSchema.text }}</div>

    <div v-if="modalSchema.extraText" class="dsaModal__extraText">{{ modalSchema.extraText }}</div>

    <div v-if="modalSchema?.inputs?.length > 0" class="dsaModal__inputBox" v-for="input in modalSchema.inputs" :key="input.name">

      <label class="dsaModal__label" :for="input.name">
        {{ input.label }}
        <span v-if="input.required" class="dsaModal__required"
          >*</span
        >
      </label>

      <component
        :is="getFieldTag(input.type)"
        v-model="input.value"
        class="dsaModal__input"
        :class="[input.extraCss, input.error ? 'dsaModal__input--error' : '']"
        
        @input="(e:any) => inputActionMethod(input, e?.target?.value)"
        v-bind="{ ...getFieldAttrs(input), ...addExtraOptions(input.name) }"
        :placeholder="input.placeholderText"
      >
        <!-- Czeka na dalszy rozwój - rozszerzyc trzeba input o kolejne typy np select -->
        <!-- <template v-if="getFieldTag(input.type) === 'select'">
          <option disabled value="">{{ input.placeholderText || input.placeholder || 'Wybierz opcję' }}</option>
          <option v-for="option in input.options || []" :key="option.id" :value="option.value">{{ option.name }}</option>
        </template> -->

      </component>

      <div v-if="input.helperText" class="dsaModal__helper">{{ input.helperText }}</div>
      <div v-if="birthdayError" class="dsaModal__error">{{ birthdayError  }}</div>
    </div>

    <div v-if="modalSchema.buttons" class="dsaModal__buttons" :class="modalSchema.buttons.length === 1 ? 'dsaModal__buttons--single' : ''">
      <div 
        v-for="btn in modalSchema.buttons"
        :key="btn.text"
        @click="proxyClickAction(btn.action)"
        class="dsaModal__button"
        :class="btn.action === 'CLOSE' ? 'dsaModal__button--ghost' : 'dsaModal__button--main'"
        :disabled="isButtonDisabled"
      >
        <span v-if="!isButtonDisabled">{{ btn.text }}</span>
        <span v-if="isButtonDisabled" class="dsa__reportLoader" :class="{'dsa__reportLoader--active': isButtonDisabled}"></span>
      </div>

      <div
        v-if="service === 'wyborcza'"
        @click="proxyClickAction('CLOSE')"
        class="dsaModal__button dsaModal__button--underline"
      > Zamknij </div>
    </div>

  </div>
</template>

<style lang="scss">
  .dsaModal {
    $m: &;

    display: flex;
    flex-direction: column;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 390px;
    z-index: 99999;
    text-align: left;
    background: #fff;
    box-shadow: 0px 19px 38px #00000042;
    padding: 30px;
    color: #111111;

    &__close{
      cursor: pointer;
      position: absolute;
      right: 15px;
      top: 15px;
      width: 20px;
      height: 20px;

      &::before {
        background: none;
          content: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='12'%20height='12'%3E%3Cpath%20fill='%23111'%20d='M10.308%200%206%204.308%201.692%200%200%201.692%204.308%206%200%2010.308%201.692%2012%206%207.692%2010.308%2012%2012%2010.308%207.692%206%2012%201.692Z'/%3E%3C/svg%3E");
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      }
    }
    &__header {
      display: block;
      font: 700 22px/29px Oswald, sans-serif;
      text-transform: uppercase;
      text-align: left;
      padding: 0 10px 0 0;
    }
    &__text,
    &__extraText {
      font: 400 14px/18px Roboto, sans-serif;
    }
    &__text{
      margin-top: 10px;
    }
    &__extraText {
      margin-top: 20px;
      color: #757575;
    }
    &__buttons {
      display: flex;
      justify-content: space-between;
      margin-top: 30px;
      gap: 0 20px;

      &--single {
        justify-content: center;

        .dsaModal__button{
          width: 100%;
        }
      }
    }
    &__button{
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
      box-sizing: border-box;
      color: #111111;
      cursor: pointer;
      font: 700 14px/14px Roboto Condensed, sans-serif;
      padding: 0 20px;
      text-transform: uppercase;
      transition: all .2s ease-out;
      white-space: nowrap;
      height: 40px;

      &--main {
        background-color: #beff96;
        border: 2px solid #beff96;
        &:hover{
          background-color: #A9F27C;
          border-color: #A9F27C;
        }
      }

      &--ghost {
          background-color: #ffffff;
          border: 2px solid #E5E5E5;

          &:hover{
            background-color: #F5F5F5;
            border-color: #D3D3D3;
          }
      }

      &--underline{
        display: none;
      }

      &:focus, &:active{
        border: 2px solid #111111;
      }

    }
    &__inputBox{
      margin-top: 30px;
    }
    &__label {
      display: block;
      font: 700 14px/18px Roboto Condensed, sans-serif;
      color: #111111;
    }
    &__input {
      // appearance: none;  //wywala defaultowy arrow
      background-color: #ffffff;
      border-radius: 0;
      color: #111111;
      font: 400 14px/18px Roboto, sans-serif;
      padding: 12px 15px;
      width: 100%;
      height: 40px;
      border: 1px solid #cccccc;
      position: relative;
      transition: border-color 0.2s ease-out;

      &:hover {
        border-color: #111;
      }

      &:focus {
        border-color: #111;
        outline: 1px solid #111;
      }

      &::placeholder {
        color: #757575;
      }

      &--error {
        border-color: #e30000 !important;
        outline-color: #e30000 !important;
      }
      &--textarea {
        display: block;
        min-height: 114px;
        margin-top: 10px;
        margin-bottom: 0;
        max-width: 100%;
        min-width: 100%;
      }

      &[type="date"] {
        appearance: auto;
        -webkit-appearance: auto;
        -moz-appearance: auto;
      }
    }
    &__required{
      color: #DC1A21;
    }
    &__helper{
      display: block;
      font: 400 12px/16px Roboto, sans-serif;
      color: #757575;
      margin: 10px 0 0;
      padding: 0;
    }
    &__error{
      font: 400 12px/16px Roboto, sans-serif;
      color: #e30000;
      margin-top: 10px;
    }
    & input[name="birthday"] {
      width: 100%;
      line-height: 15px;
    }
  }

  .dsa__widget--Sport .dsaModal {
    &__button{
      &--main {
        background-color: #f03c3c;
        border: 2px solid #f03c3c;
        color: #ffffff;
        &:hover{
          background-color: #DB2222;
          border-color: #DB2222;
        }
      }
    }
  }

  @media all and (max-width: 932px) {
    .dsaModal {
      $m: &;

      border-radius: 0;
      width: 100vw;
      padding: 30px 15px;
      top: 0;
      left: 0;
      transform: translate(0, 0);

      &__buttons{
        gap: 20px;
      }
    }
  }
</style>
